import React, { useMemo, useState } from "react";
import {
  ArrowRight,
  Shield,
  Smartphone,
  LineChart,
  Target,
  CheckCircle2,
  ChevronDown,
  PieChart,
  BarChart3,
  Rocket,
  PlayCircle,
  Wallet,
  Sparkles,
} from "lucide-react";

// Animations removed to ensure compatibility with current build setup

export function Hero() {
  return (
    <section id="home" className="py-5 bg-light">
      <div className="container">
        <div className="row align-items-center g-4">
          <div className="col-12 col-md-6">
            <span className="badge bg-white text-dark border d-inline-flex align-items-center gap-2">
              <Sparkles size={14} /> New: Goal-based planning made effortless
            </span>
            <h1 className="mt-3 display-5 fw-bold">
              Plan. Track. <span className="text-primary">Grow</span> your wealth.
            </h1>
            <p className="mt-3 lead text-muted">
              BuildFinanceGoals brings all your investments into one clean dashboard, helps you set clear goals, and gives actionable insights—so you can focus on what truly matters.
            </p>
            <div className="mt-4 d-flex flex-wrap gap-2">
              <a href="#cta" className="btn btn-dark d-inline-flex align-items-center gap-2">
                Start free <ArrowRight size={16} />
              </a>
              <a href="#demo" className="btn btn-outline-secondary d-inline-flex align-items-center gap-2">
                <PlayCircle size={16} /> Watch demo
              </a>
            </div>
            <div className="row mt-4 text-muted">
              <div className="col">
                <div className="h5">One place</div>
                <div>Track MF, stocks, gold & more</div>
              </div>
              <div className="col">
                <div className="h5">Goal-first</div>
                <div>Plan for house, kids, retirement</div>
              </div>
              <div className="col">
                <div className="h5">Secure</div>
                <div>Bank-grade encryption</div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="card shadow">
              <div className="card-body bg-dark text-white rounded-3">
                <div className="d-flex justify-content-between small text-white-50">
                  <span>Your portfolio</span>
                  <span className="badge bg-secondary">Live</span>
                </div>
                <div className="row text-center mt-3">
                  <div className="col">
                    <div className="small text-white-50">Value</div>
                    <div className="fs-5 fw-semibold">₹ 12.8L</div>
                  </div>
                  <div className="col">
                    <div className="small text-white-50">1D</div>
                    <div className="fs-5 fw-semibold">+0.82%</div>
                  </div>
                  <div className="col">
                    <div className="small text-white-50">YTD</div>
                    <div className="fs-5 fw-semibold">+11.4%</div>
                  </div>
                </div>
                <div className="mt-4 bg-secondary bg-opacity-25 rounded" style={{ height: 110 }} />
                <div className="row row-cols-3 g-2 mt-3 small">
                  {[
                    { Icon: PieChart, label: "Equity Assets" },
                    { Icon: BarChart3, label: "Fixed Assets" },
                    { Icon: Wallet, label: "Cash" },
                  ].map((x) => (
                    <div key={x.label} className="d-flex align-items-center gap-2 bg-white bg-opacity-10 p-2 rounded">
                      <x.Icon size={16} />
                      <span>{x.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeatureCard({ title, desc, Icon }: { title: string; desc: string; Icon: React.ElementType }) {
  return (
    <div className="rounded-3 border border-1 bg-white p-4 shadow-sm h-100">
      <div className="d-flex align-items-center gap-2">
        <div className="bg-primary text-white rounded p-2 d-inline-flex">
          <Icon size={18} />
        </div>
        <h3 className="h5 m-0">{title}</h3>
      </div>
      <p className="mt-2 small text-muted">{desc}</p>
      <div className="mt-2 d-inline-flex align-items-center gap-2 text-primary small">
        Learn more <ArrowRight size={14} />
      </div>
    </div>
  );
}

export function Features() {
  const features = [
    { title: "Track everything", desc: "See mutual funds, stocks, gold, and cash in one dashboard. Auto-import or add manually.", Icon: LineChart },
    { title: "Goal-first planning", desc: "Create goals for home, education, or retirement. Get SIP suggestions that fit your timeline.", Icon: Target },
    { title: "Smart insights", desc: "Get rebalancing nudges and risk checks so your plan stays on course—without overthinking.", Icon: Rocket },
    { title: "Bank-grade security", desc: "Your data is encrypted in transit and at rest. You stay in control of what you share.", Icon: Shield },
  ];
  return (
    <section id="features" className="py-5">
      <div className="container">
  <div>
          <div className="text-center mx-auto" style={{ maxWidth: 720 }}>
            <h2 className="h1 fw-bold">Everything you need to build wealth—beautifully organized</h2>
            <p className="text-muted mt-2">Inspired by the best of modern finance apps with a clean, goal-oriented experience.</p>
          </div>
          <div className="row g-3 mt-3">
            {features.map((f) => (
              <div className="col-12 col-sm-6 col-lg-3" key={f.title}>
                <FeatureCard {...f} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CurrencyINR({ value }: { value: number }) {
  const formatted = useMemo(() => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(value || 0), [value]);
  return <span>{formatted}</span>;
}

export function SIPPlanner() {
  const [monthly, setMonthly] = useState(10000);
  const [years, setYears] = useState(10);
  const [expected, setExpected] = useState(12);

  const result = useMemo(() => {
    const m = monthly;
    const n = years * 12;
    const r = expected / 100 / 12;
    const fv = m * (((Math.pow(1 + r, n) - 1) / r) * (1 + r));
    const invested = m * n;
    return { fv, invested, gain: fv - invested };
  }, [monthly, years, expected]);

  return (
    <section id="planner" className="py-5">
      <div className="container">
        <div className="row align-items-center g-4">
          <div className="col-12 col-md-6">
            <h2 className="h1 fw-bold">Plan your goals with a simple SIP</h2>
            <p className="text-muted mt-2">
              Use the interactive planner to estimate how your monthly investments can grow over time. Adjust your monthly amount, time horizon, and expected returns.
            </p>
            <ul className="list-unstyled mt-3 small">
              <li className="d-flex align-items-center gap-2"><CheckCircle2 size={16} className="text-primary" /> Goal-first suggestions</li>
              <li className="d-flex align-items-center gap-2"><CheckCircle2 size={16} className="text-primary" /> Rebalancing nudges</li>
              <li className="d-flex align-items-center gap-2"><CheckCircle2 size={16} className="text-primary" /> Clean, unified dashboard</li>
            </ul>
          </div>
          <div className="col-12 col-md-6">
            <div className="card shadow-sm">
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label">Monthly investment (₹)</label>
                  <input type="range" min={1000} max={100000} step={1000} value={monthly} onChange={(e) => setMonthly(parseInt(e.target.value))} className="form-range" />
                  <div className="d-flex justify-content-between small text-muted"><span>₹1,000</span><span>₹1,00,000</span></div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Years</label>
                  <input type="range" min={1} max={30} step={1} value={years} onChange={(e) => setYears(parseInt(e.target.value))} className="form-range" />
                  <div className="d-flex justify-content-between small text-muted"><span>1</span><span>30</span></div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Expected return (p.a. %)</label>
                  <input type="range" min={5} max={18} step={0.5} value={expected} onChange={(e) => setExpected(parseFloat(e.target.value))} className="form-range" />
                  <div className="d-flex justify-content-between small text-muted"><span>5%</span><span>18%</span></div>
                </div>
                <div className="row text-center g-2">
                  <div className="col">
                    <div className="card bg-light">
                      <div className="card-body p-2">
                        <div className="small text-muted">Invested</div>
                        <div className="fw-semibold"><CurrencyINR value={result.invested} /></div>
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="card bg-light">
                      <div className="card-body p-2">
                        <div className="small text-muted">Est. returns</div>
                        <div className="fw-semibold"><CurrencyINR value={result.gain} /></div>
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="card bg-light">
                      <div className="card-body p-2">
                        <div className="small text-muted">Est. corpus</div>
                        <div className="fw-semibold"><CurrencyINR value={result.fv} /></div>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="mt-2 small text-muted">This is a simple illustration, not investment advice. Actual performance may vary.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Steps() {
  const steps = [
    { title: "Connect & import", desc: "Securely import statements or add holdings manually to get a unified view.", Icon: Smartphone },
    { title: "Set goals", desc: "Define clear milestones—home, education, retirement—then pick a plan you can stick to.", Icon: Target },
    { title: "Track & optimize", desc: "Get smart nudges to rebalance, manage risk, and stay on track for each goal.", Icon: LineChart },
  ];
  return (
    <section id="how" className="py-5">
      <div className="container">
        <div className="text-center mx-auto" style={{ maxWidth: 720 }}>
          <h2 className="h1 fw-bold">From scattered accounts to a single, clear plan</h2>
          <p className="text-muted mt-2">Three simple steps to start building towards your goals.</p>
        </div>
    <div className="row g-3 mt-3">
          {steps.map((s, i) => (
            <div className="col-12 col-md-4" key={s.title}>
      <div className="card h-100 shadow-sm position-relative">
                <div className="position-absolute top-0 start-0 translate-middle badge rounded-pill bg-primary">Step {i + 1}</div>
                <div className="card-body">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div className="bg-primary text-white rounded p-2 d-inline-flex"><s.Icon size={18} /></div>
                    <h3 className="h5 m-0">{s.title}</h3>
                  </div>
                  <p className="text-muted small mb-0">{s.desc}</p>
                </div>
      </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Security() {
  return (
    <section id="security" className="py-5">
      <div className="container">
        <div className="p-4 p-md-5 bg-dark text-white rounded-3">
          <div className="row align-items-center g-3">
            <div className="col">
              <h3 className="h3">Security you can trust</h3>
              <p className="mb-0 text-white-50">We use encryption in transit and at rest, role-based access, and continuous monitoring. You control what you connect and can disconnect anytime.</p>
            </div>
            <div className="col-auto d-flex gap-2">
              <span className="badge bg-secondary d-inline-flex align-items-center gap-2"><Shield size={18} /> 256-bit encryption</span>
              <span className="badge bg-secondary d-inline-flex align-items-center gap-2"><CheckCircle2 size={18} /> Read-only connections</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Testimonial({ quote, name }: { quote: string; name: string }) {
  return (
    <div className="rounded-2xl border border-neutral-200 dark:border-white/10 bg-white dark:bg-neutral-900 p-6 shadow-sm">
      <p className="text-sm leading-relaxed">“{quote}”</p>
      <div className="mt-4 text-sm font-medium text-neutral-600 dark:text-neutral-300">— {name}</div>
    </div>
  );
}

export function Testimonials() {
  const quotes = [
    { quote: "The cleanest view of all my investments—finally goal-focused.", name: "Aarav S." },
    { quote: "The SIP planner made it super easy to set targets and stay consistent.", name: "Meera T." },
    { quote: "I love the gentle nudges to rebalance without being overwhelmed.", name: "Rishabh K." },
  ];
  return (
    <section className="py-5">
      <div className="container">
        <div className="text-center mx-auto" style={{ maxWidth: 720 }}>
          <h2 className="h1 fw-bold">Loved by goal-driven investors</h2>
          <p className="text-muted mt-2">A modern experience that removes friction and builds confidence.</p>
        </div>
        <div className="row g-3 mt-3">
          {quotes.map((q) => (
            <div className="col-12 col-md-4" key={q.name}>
              <Testimonial {...q} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FAQ() {
  const [open, setOpen] = useState<number | null>(null);
  const faqs = [
    { q: "Is BuildFinanceGoals free to start?", a: "Yes. You can start for free and upgrade for advanced insights when you’re ready." },
    { q: "How do you import my investments?", a: "You can upload statements or connect supported accounts for read-only import. You always control access." },
    { q: "Is my data secure?", a: "We use encryption in transit and at rest, and employ strict access controls and monitoring." },
    { q: "Do you give investment advice?", a: "We provide tools and education to help you plan and track goals. We are not a broker or advisor." },
  ];
  return (
    <section id="faq" className="py-5">
      <div className="container">
        <div className="text-center mx-auto" style={{ maxWidth: 720 }}>
          <h2 className="h1 fw-bold">Frequently asked questions</h2>
          <p className="text-muted mt-2">Everything you need to know about getting started.</p>
        </div>
        <div className="mx-auto mt-4" style={{ maxWidth: 900 }}>
          {faqs.map((f, i) => (
            <div className="border rounded-3 mb-2" key={i}>
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-100 text-start btn btn-link text-decoration-none px-4 py-3"
                aria-expanded={open === i}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div className="fw-medium">{f.q}</div>
                  <ChevronDown style={{ transition: 'transform .2s', transform: open === i ? 'rotate(180deg)' : 'none' }} />
                </div>
              </button>
              {open === i && (
                <div className="px-4 pb-3 text-muted small">{f.a}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CTA() {
  return (
    <section id="cta" className="py-5">
      <div className="container">
        <div className="text-center p-5 bg-white border rounded-3 shadow-sm">
          <h2 className="h1 fw-bold">Ready to build your finance goals?</h2>
          <p className="text-muted mt-2">Start free. It takes less than 2 minutes.</p>
          <div className="mt-3 d-flex justify-content-center gap-2">
            <a href="#" className="btn btn-primary d-inline-flex align-items-center gap-2">
              Create your account <ArrowRight size={16} />
            </a>
            <a href="#" className="btn btn-outline-secondary d-inline-flex align-items-center gap-2">
              <Smartphone size={16} /> Get the app
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function BuildFinanceHomeSections() {
  return (
    <>
      <Hero />
      <Features />
      <Steps />
      <Security />
      <FAQ />
      <CTA />
    </>
  );
}
